import type { Cue } from '../model/cue.ts';

/** How far ahead of cue.start to keep upcoming blocks in the DOM. */
export const DEFAULT_DUBBING_LOOKAHEAD_MS = 4500;

/** How long after cue.end to keep past blocks before culling. */
export const DEFAULT_DUBBING_LOOKBACK_FACTOR = 2;

/** Minimum visual gap between consecutive cues on the tape (px). */
export const DUBBING_CUE_GAP_PX = 12;

export type DubbingLayoutInput = {
  viewWidth: number;
  spaceWidth: number;
  currentTimeMs: number;
  lookAheadMs?: number;
  lookBackFactor?: number;
};

export type DubbingScale = {
  /** Horizontal center of the letterboxed video content (overlay coords). */
  centerX: number;
  /** px/ms for empty timeline (approach / inter-cue gaps / post-end exit). */
  approachScale: number;
  lookAheadMs: number;
  lookBackMs: number;
};

export type DubbingTapeCue = {
  start: number;
  end: number;
  width: number;
};

export type DubbingTapePlacement = DubbingTapeCue & {
  /** Left edge of the text block on the continuous tape (px). */
  tapeStart: number;
  /** Right edge of the text block on the continuous tape (px). */
  tapeEnd: number;
  /**
   * Playhead position at cue.end (px). Equals tapeEnd for the last cue;
   * otherwise includes a trailing {@link DUBBING_CUE_GAP_PX} so consecutive
   * blocks stay separated without a discontinuous handoff jump.
   */
  tapeSpanEnd: number;
};

/** Center line + visibility span (no independent scroll-speed setting). */
export function dubbingScale(input: DubbingLayoutInput): DubbingScale {
  const lookAheadMs = input.lookAheadMs ?? DEFAULT_DUBBING_LOOKAHEAD_MS;
  const lookBackFactor = input.lookBackFactor ?? DEFAULT_DUBBING_LOOKBACK_FACTOR;
  const half = Math.max(0, input.viewWidth) / 2;
  return {
    centerX: input.spaceWidth + half,
    approachScale: lookAheadMs > 0 ? half / lookAheadMs : 0,
    lookAheadMs,
    lookBackMs: lookAheadMs * lookBackFactor,
  };
}

/** Keep temporary center offset inside the letterboxed content. */
export function clampCenterOffsetPx(offsetPx: number, viewWidth: number): number {
  const half = Math.max(0, viewWidth) / 2;
  return Math.min(half, Math.max(-half, offsetPx));
}

/** Natural centerX plus a session offset (clamped to content). */
export function centerXWithOffset(
  spaceWidth: number,
  viewWidth: number,
  offsetPx: number,
): number {
  const { centerX } = dubbingScale({ viewWidth, spaceWidth, currentTimeMs: 0 });
  return centerX + clampCenterOffsetPx(offsetPx, viewWidth);
}

/**
 * Lay cues on a continuous horizontal tape:
 * each block keeps its width; positive time gaps become empty space
 * (timeGap × approachScale). Every cue except the last also carries a
 * trailing {@link DUBBING_CUE_GAP_PX} that the playhead scrolls through
 * during that cue — so abutting / overlapping / tiny-gap sentences stay
 * visually separated without a discontinuous jump at handoff.
 */
export function buildDubbingTape(
  cues: DubbingTapeCue[],
  approachScale: number,
  minGapPx: number = DUBBING_CUE_GAP_PX,
): DubbingTapePlacement[] {
  const sorted = [...cues].sort((a, b) => a.start - b.start || a.end - b.end);
  const out: DubbingTapePlacement[] = [];
  let cursor = 0;

  for (let i = 0; i < sorted.length; i += 1) {
    const cue = sorted[i]!;
    const width = Math.max(0, cue.width);

    if (i > 0) {
      const prev = sorted[i - 1]!;
      const timeGap = cue.start - prev.end;
      if (timeGap > 0 && approachScale > 0) {
        cursor += timeGap * approachScale;
      }
      // timeGap <= 0: no timed gap; trailing minGap from the previous cue separates.
    }

    const tapeStart = cursor;
    const tapeEnd = cursor + width;
    const trailingGap = i < sorted.length - 1 ? minGapPx : 0;
    const tapeSpanEnd = tapeEnd + trailingGap;
    out.push({
      start: cue.start,
      end: cue.end,
      width,
      tapeStart,
      tapeEnd,
      tapeSpanEnd,
    });
    cursor = tapeSpanEnd;
  }

  return out;
}

/**
 * Playhead on the tape at time t.
 * Inside a cue: advances through that cue's span (width + trailing gap) over [start, end].
 * Between cues: advances through any remaining timed tape gap over the time gap.
 */
export function tapePlayheadX(
  tape: DubbingTapePlacement[],
  currentTimeMs: number,
  approachScale: number,
): number {
  if (tape.length === 0) return 0;

  const first = tape[0]!;
  if (currentTimeMs < first.start) {
    const scale = approachScale > 0 ? approachScale : 0;
    return first.tapeStart - (first.start - currentTimeMs) * scale;
  }

  // Prefer the last cue that still contains t (handles mild overlaps).
  let activeIndex = -1;
  for (let i = 0; i < tape.length; i += 1) {
    const cue = tape[i]!;
    if (cue.start <= currentTimeMs && currentTimeMs <= cue.end) {
      activeIndex = i;
    }
  }
  if (activeIndex >= 0) {
    const cue = tape[activeIndex]!;
    const duration = Math.max(cue.end - cue.start, 1);
    const progress = (currentTimeMs - cue.start) / duration;
    return cue.tapeStart + progress * (cue.tapeSpanEnd - cue.tapeStart);
  }

  for (let i = 0; i < tape.length; i += 1) {
    const cue = tape[i]!;
    const next = tape[i + 1];
    if (next && currentTimeMs > cue.end && currentTimeMs < next.start) {
      const gapDuration = next.start - cue.end;
      const gapWidth = next.tapeStart - cue.tapeSpanEnd;
      if (gapDuration <= 0) return next.tapeStart;
      const progress = (currentTimeMs - cue.end) / gapDuration;
      return cue.tapeSpanEnd + progress * gapWidth;
    }
  }

  const last = tape[tape.length - 1]!;
  if (currentTimeMs > last.end) {
    const scale = approachScale > 0 ? approachScale : 0;
    return last.tapeSpanEnd + (currentTimeMs - last.end) * scale;
  }

  return first.tapeStart;
}

/** Screen left edge: playhead locks to centerX. */
export function cueScreenLeftX(
  tapeStart: number,
  playheadX: number,
  centerX: number,
): number {
  return centerX - (playheadX - tapeStart);
}

/**
 * Local tape speed (px/ms) at t — used to map horizontal drag to seek delta.
 */
export function localTapeScalePxPerMs(
  tape: DubbingTapePlacement[],
  currentTimeMs: number,
  approachScale: number,
): number {
  if (tape.length === 0) return approachScale;

  const first = tape[0]!;
  if (currentTimeMs < first.start) {
    return approachScale > 0 ? approachScale : 0;
  }

  let activeIndex = -1;
  for (let i = 0; i < tape.length; i += 1) {
    const cue = tape[i]!;
    if (cue.start <= currentTimeMs && currentTimeMs <= cue.end) {
      activeIndex = i;
    }
  }
  if (activeIndex >= 0) {
    const cue = tape[activeIndex]!;
    return (cue.tapeSpanEnd - cue.tapeStart) / Math.max(cue.end - cue.start, 1);
  }

  for (let i = 0; i < tape.length; i += 1) {
    const cue = tape[i]!;
    const next = tape[i + 1];
    if (next && currentTimeMs > cue.end && currentTimeMs < next.start) {
      const gapDuration = next.start - cue.end;
      const gapWidth = next.tapeStart - cue.tapeSpanEnd;
      if (gapDuration <= 0) return approachScale;
      return gapWidth / gapDuration;
    }
  }

  return approachScale > 0 ? approachScale : 0;
}

/**
 * How many pixels of the block sit left of the center line (gray portion).
 * Clamped to [0, blockWidth].
 */
export function pastClipWidth(
  blockLeftX: number,
  blockWidth: number,
  centerX: number,
): number {
  if (blockWidth <= 0) return 0;
  return Math.min(blockWidth, Math.max(0, centerX - blockLeftX));
}

/**
 * Horizontal drag (+right) → match-time delta (ms).
 * screenLeft = center - (playhead - tapeStart) ⇒ d(playhead) = -dx.
 */
export function matchTimeDeltaFromDragPx(
  dxPx: number,
  localScalePxPerMs: number,
): number {
  if (!Number.isFinite(dxPx) || !(localScalePxPerMs > 0)) return 0;
  return -dxPx / localScalePxPerMs;
}

/** Visible cues spanning upcoming approach through post-end exit. */
export function visibleDubbingCues(
  cues: Cue[],
  currentTimeMs: number,
  lookAheadMs: number,
  lookBackMs: number,
): Cue[] {
  return cues.filter(
    (cue) => cue.start - lookAheadMs <= currentTimeMs
      && currentTimeMs <= cue.end + lookBackMs,
  );
}
